import { computed, type WritableComputedRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

type Options = {
  defaultValue?: string | null;
  method?: 'push' | 'replace';
};

/**
 * Two-way binds a reactive value to a URL query parameter.
 * Used to make selection state survive browser back/forward navigation.
 */
export function useRouteQueryParam(
  key: string,
  options: Options = {},
): WritableComputedRef<string | null> {
  const route = useRoute();
  const router = useRouter();
  const { defaultValue = null, method = 'replace' } = options;

  return computed({
    get(): string | null {
      const raw = route.query[key];
      const value = Array.isArray(raw) ? raw[0] : raw;
      return value ?? defaultValue;
    },
    set(value: string | null): void {
      const current = route.query[key];
      const currentValue = Array.isArray(current) ? current[0] : current;
      if ((currentValue ?? null) === (value ?? null)) return;

      const nextQuery = { ...route.query };
      if (value === null || value === '') {
        delete nextQuery[key];
      } else {
        nextQuery[key] = value;
      }
      router[method]({ query: nextQuery });
    },
  });
}
